import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/use-toast';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { fetchPilotStatus, updatePilotStatus } from '@/lib/marketExperienceApi';
import { invalidatePilotStatus } from '@/hooks/usePilotStatus';

export default function PilotModeControl({ currentTenant }) {
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const { toast } = useToast();
  const tenantId = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant || contextTenant,
    contextUser,
    'instructure'
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pilotMode, setPilotMode] = useState(false);
  const [cap, setCap] = useState(10);
  const [label, setLabel] = useState(null);

  useEffect(() => {
    if (!tenantId) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchPilotStatus(tenantId)
      .then((data) => {
        if (cancelled) return;
        setPilotMode(Boolean(data?.pilot_mode));
        const nextCap = Number(data?.cap);
        setCap(Number.isFinite(nextCap) && nextCap > 0 ? nextCap : 10);
        setLabel(data?.label ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setPilotMode(false);
          setLabel(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tenantId]);

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    try {
      const data = await updatePilotStatus(tenantId, {
        pilot_mode: pilotMode,
        cap: Number(cap) || 10,
      });
      setPilotMode(Boolean(data?.pilot_mode));
      const nextCap = Number(data?.cap);
      if (Number.isFinite(nextCap) && nextCap > 0) setCap(nextCap);
      setLabel(data?.label ?? null);
      invalidatePilotStatus(tenantId);
      toast({ title: 'Pilot settings saved' });
    } catch (e) {
      toast({
        title: 'Could not save pilot settings',
        description: e?.message || 'The API rejected this update.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-[#0A1A2F]">Pilot mode</CardTitle>
        <CardDescription>
          Caps how many accounts this tenant can open. Customers never see this switch — only the effects.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-[#5C6B7A]">Tenant: {tenantId}</p>
        {loading ? (
          <div className="h-16 bg-gray-100 rounded-md animate-pulse" />
        ) : (
          <>
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="pilot-mode" className="text-sm text-[#0A1A2F]">Pilot mode</Label>
              <Switch
                id="pilot-mode"
                checked={pilotMode}
                onCheckedChange={setPilotMode}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pilot-cap" className="text-sm text-[#0A1A2F]">Account cap</Label>
              <Input
                id="pilot-cap"
                type="number"
                min={1}
                className="w-28"
                value={cap}
                onChange={(e) => setCap(e.target.value)}
              />
            </div>
            {label ? (
              <p className="text-xs text-[#5C6B7A]">{label}</p>
            ) : null}
            <Button
              className="bg-[#00C2C2] hover:bg-[#00A8A8] text-white"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
